import { Button, Modal, Tooltip } from '@cartwheel/components'
import { useState } from 'react'

export interface ClinicalDigest {
  id: string
  raw_response: Record<string, string>
  attended_count: number
  no_show_count: number
  cancellation_count: number
  at_risk: boolean
  published_at: string | null
  published_by_name: string | null
  updated_at: string | null
  next_update_date: string | null
  quality_overall_score: number | null
  quality_verdict: 'pass' | 'needs_review' | 'fail' | null
}

interface Props {
  digest?: ClinicalDigest | null
  nextUpdateDate?: string | null
  noUpcomingAppointments?: boolean
  notEnoughData?: boolean
  summaryNotApplicable?: boolean
  summaryNotApplicableMessage?: string
  errorMessage?: string | null
  canPublishDigests?: boolean
  hasSchoolRoi?: boolean
  onPublish?: (() => void) | null
  onUnpublish?: (() => void) | null
  isMutating?: boolean
}

const CARD_SHELL =
  'mb-6 rounded-lg bg-white-dark px-6 py-4 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.06),0px_2px_8px_0px_rgba(0,0,0,0.08)]'

const VERDICT_LABELS: Record<NonNullable<ClinicalDigest['quality_verdict']>, string> = {
  pass: 'PASS',
  needs_review: 'NEEDS REVIEW',
  fail: 'FAIL',
}

export default function ClinicalDigestCard({
  digest = null,
  nextUpdateDate = null,
  noUpcomingAppointments = false,
  notEnoughData = false,
  summaryNotApplicable = false,
  summaryNotApplicableMessage = 'Not available for this care type',
  errorMessage = null,
  canPublishDigests = false,
  hasSchoolRoi = false,
  onPublish = null,
  onUnpublish = null,
  isMutating = false,
}: Props) {
  const headerNextUpdateDate = digest?.next_update_date ?? nextUpdateDate
  const hasPublishControls = digest && canPublishDigests

  if (!digest && summaryNotApplicable && !errorMessage) {
    return (
      <div className="mb-6 rounded-lg bg-white-dark px-6 py-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="flex items-center gap-4">
          <p className="text-base font-semibold leading-normal text-charcoal-75">Monthly Summary</p>
          <div className="flex items-center gap-1">
            <i
              className="fa-sharp fa-solid fa-sparkles text-base text-charcoal-25"
              aria-hidden="true"
            />
            <span className="text-sm font-normal leading-normal text-charcoal-50">
              AI-generated
            </span>
          </div>
        </div>
        <p className="text-sm font-normal leading-normal text-charcoal-50">
          {summaryNotApplicableMessage}
        </p>
      </div>
    )
  }

  if (hasPublishControls) {
    const isDraft = !digest.published_at
    const visibleToSchool = !isDraft && hasSchoolRoi
    const wrapperBg = visibleToSchool ? 'bg-mint-50' : 'bg-lavender-50'
    return (
      <div
        className={`mb-6 px-1 pb-1 rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.06),0px_2px_8px_0px_rgba(0,0,0,0.08)] ${wrapperBg}`}
      >
        <PublishControls
          digest={digest}
          hasSchoolRoi={hasSchoolRoi}
          onPublish={onPublish}
          onUnpublish={onUnpublish}
          isMutating={isMutating}
        />
        <div className="rounded-lg bg-white-dark px-6 py-4 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.06),0px_2px_8px_0px_rgba(0,0,0,0.08)]">
          <DigestHeader
            updatedAt={digest.updated_at}
            nextUpdateDate={headerNextUpdateDate}
            noUpcomingAppointments={noUpcomingAppointments}
          />
          <DigestContent digest={digest} />
        </div>
      </div>
    )
  }

  return (
    <div className={CARD_SHELL}>
      <DigestHeader
        updatedAt={digest?.updated_at ?? null}
        nextUpdateDate={headerNextUpdateDate}
        noUpcomingAppointments={noUpcomingAppointments}
      />

      {digest && <DigestContent digest={digest} />}

      {!digest && errorMessage && <EmptyState message={errorMessage} />}

      {!digest && !notEnoughData && !errorMessage && (
        <EmptyState message="Summary not available yet." />
      )}

      {!digest && notEnoughData && !errorMessage && (
        <EmptyState message={'Summaries generate automatically\nafter 30 days in care'} />
      )}
    </div>
  )
}

function PublishControls({
  digest,
  hasSchoolRoi,
  onPublish,
  onUnpublish,
  isMutating,
}: {
  digest: ClinicalDigest
  hasSchoolRoi: boolean
  onPublish?: (() => void) | null
  onUnpublish?: (() => void) | null
  isMutating: boolean
}) {
  const isDraft = !digest.published_at
  const [showConfirm, setShowConfirm] = useState(false)

  const headerText = isDraft
    ? 'Pending review \u2014 not yet shared with school'
    : hasSchoolRoi
      ? 'Published to school'
      : 'Published \u2014 Internal only (no ROI)'

  return (
    <div className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-xl font-bold leading-normal text-charcoal-75">{headerText}</p>
        {isDraft ? (
          <div className="flex items-center gap-[10px] text-sm text-charcoal-75">
            <i className="fa-regular fa-eye" aria-hidden="true" />
            <span>Visible to care team only</span>
          </div>
        ) : (
          <p className="text-sm font-normal leading-normal text-charcoal-75">
            {[
              digest.published_by_name && `by ${digest.published_by_name}`,
              digest.published_at && formatDigestDate(digest.published_at),
            ]
              .filter(Boolean)
              .join(' \u00b7 ')}
          </p>
        )}
      </div>

      {isDraft && onPublish && (
        <div className="flex flex-col items-end gap-2">
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={isMutating}
            className="bg-indigo border-none"
          >
            <i className="fa-sharp fa-solid fa-check" aria-hidden="true" />
            {isMutating ? 'Publishing\u2026' : 'Publish to school'}
          </Button>
          {digest.quality_verdict && VERDICT_LABELS[digest.quality_verdict] && (
            <p className="text-sm font-semibold text-charcoal-75">
              {VERDICT_LABELS[digest.quality_verdict]}
              {digest.quality_overall_score !== null && ` (${digest.quality_overall_score}%)`}
            </p>
          )}
          <Modal
            isOpen={showConfirm}
            onClose={() => setShowConfirm(false)}
            title="Share with school?"
          >
            <p className="text-base leading-normal text-charcoal">
              School counselors and staff will be able to view this.
            </p>
            <div className="flex items-center justify-end gap-2 pt-4">
              <Button variant="tertiary" onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={isMutating}
                onClick={() => {
                  setShowConfirm(false)
                  onPublish()
                }}
              >
                {isMutating ? 'Publishing\u2026' : 'Yes, publish to school'}
              </Button>
            </div>
          </Modal>
        </div>
      )}

      {!isDraft && onUnpublish && (
        <Button variant="secondary" onClick={onUnpublish} disabled={isMutating}>
          {isMutating ? 'Unpublishing\u2026' : 'Unpublish'}
        </Button>
      )}
    </div>
  )
}

function DigestHeader({
  updatedAt,
  nextUpdateDate,
  noUpcomingAppointments,
}: {
  updatedAt: string | null
  nextUpdateDate: string | null
  noUpcomingAppointments: boolean
}) {
  const dateParts: string[] = []
  if (updatedAt) dateParts.push(`Updated ${formatDigestDate(updatedAt)}`)
  if (nextUpdateDate) dateParts.push(`Next update ${formatDigestDate(nextUpdateDate)}`)
  if (noUpcomingAppointments) dateParts.push('Not scheduled - no upcoming sessions')

  return (
    <div className="flex flex-col gap-2 border-b border-gray-stroke pb-6 md:flex-row md:items-center md:justify-between md:gap-4">
      <div className="flex items-center gap-4">
        <p className="text-base font-semibold leading-normal text-charcoal-75">Monthly Summary</p>
        <div className="flex items-center gap-1">
          <i
            className="fa-sharp fa-solid fa-sparkles text-base text-charcoal-25"
            aria-hidden="true"
          />
          <span className="text-sm font-normal leading-normal text-charcoal-50">AI-generated</span>
        </div>
      </div>
      {dateParts.length > 0 && (
        <p className="text-sm font-normal leading-normal text-charcoal-50">
          {dateParts.join(' \u2022 ')}
        </p>
      )}
    </div>
  )
}

function DigestContent({ digest }: { digest: ClinicalDigest }) {
  const sections = Object.entries(digest.raw_response)

  return (
    <div className="flex flex-col pt-4 md:flex-row md:min-h-[170px]">
      {sections.map(([title, markdown]) => (
        <div
          key={title}
          className="flex flex-1 flex-col gap-2 self-stretch border-b border-gray-stroke px-6 py-4 md:border-b-0 md:border-r md:py-0"
        >
          <p className="text-sm font-semibold leading-normal text-charcoal-75">{title}</p>
          {renderMarkdownBullets(markdown)}
        </div>
      ))}

      <AttendanceSection digest={digest} />
    </div>
  )
}

function AttendanceSection({ digest }: { digest: ClinicalDigest }) {
  return (
    <div className="flex flex-1 flex-col gap-[15px] self-stretch px-6 py-4 md:py-0">
      {digest.at_risk && (
        <div className="flex justify-end">
          <div className="flex items-center gap-2 rounded-2xl px-2 py-1 bg-orange-25">
            <i
              className="fa-sharp fa-solid fa-arrow-trend-down text-xs text-brick"
              aria-hidden="true"
            />
            <span className="text-sm font-medium leading-normal text-brick">
              At risk of discharge
            </span>
          </div>
        </div>
      )}
      <div className="flex flex-col text-sm leading-normal">
        <p className="font-semibold text-charcoal-75">Attendance</p>
        <p className="font-medium text-charcoal-50">(past month)</p>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium leading-normal text-charcoal-75">Completed</span>
          <span className="text-lg font-bold leading-normal text-forest">
            {digest.attended_count}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium leading-normal text-charcoal-75">Missed</span>
            <Tooltip
              delay={0}
              text="This includes both excused and unexcused no shows, as well as late cancellations"
            >
              <i className="fa-regular fa-circle-info text-sm text-charcoal-50 cursor-help" />
            </Tooltip>
          </div>
          <span
            className={`text-lg font-bold leading-normal ${digest.no_show_count > 0 ? 'text-orange' : 'text-charcoal-25'}`}
          >
            {digest.no_show_count}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium leading-normal text-charcoal-75">Cancellations</span>
          <span
            className={`text-lg font-bold leading-normal ${digest.cancellation_count > 0 ? 'text-orange' : 'text-charcoal-25'}`}
          >
            {digest.cancellation_count}
          </span>
        </div>
      </div>
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10">
      <div className="mb-2 flex items-end gap-0.5 text-charcoal-25">
        <i className="fa-solid fa-file text-2xl -rotate-12" aria-hidden="true" />
        <i className="fa-solid fa-file text-2xl rotate-3" aria-hidden="true" />
        <i className="fa-solid fa-file text-2xl rotate-12" aria-hidden="true" />
      </div>
      <p className="text-center text-sm leading-normal text-charcoal-50">{message}</p>
    </div>
  )
}

function renderMarkdownBullets(markdown: string) {
  const lines = markdown
    .split('\n')
    .map((line) => line.replace(/^[-*]\s*/, '').trim())
    .filter(Boolean)

  return (
    <ul className="flex flex-col gap-2">
      {lines.map((line) => (
        <li
          key={line}
          className="list-disc ms-[21px] text-sm font-medium leading-normal text-charcoal-50"
        >
          {line}
        </li>
      ))}
    </ul>
  )
}

function formatDigestDate(dateString: string): string {
  const datePart = dateString.slice(0, 10)
  const parts = datePart.split('-').map(Number)
  const year = parts[0] ?? 0
  const month = (parts[1] ?? 1) - 1
  const day = parts[2] ?? 1
  const date = new Date(year, month, day)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
